import React from 'react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { CALL_TYPE_COLORS, CALL_TYPE_LABELS, type LlmInspectorCall } from './llmInspectorData';
import { useObsTheme } from './theme';
import { SpanStatusDot, SpanStatusText } from './ui';

interface Props {
  calls: LlmInspectorCall[];
  onSelectCall: (call: LlmInspectorCall) => void;
}

export default function LlmInspectorCallList({ calls, onSelectCall }: Props) {
  const t = useObsTheme();

  return (
    <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
      <table className="min-w-full text-left">
        <thead className={`sticky top-0 z-[1] ${t.tableHead}`}>
          <tr>
            {['#', 'Type', 'From → To', 'Call', 'Summary', 'Duration', 'Tokens', 'Cost', 'Status', ''].map((h) => (
              <th
                key={h}
                className={`px-2.5 py-2 text-[10px] font-semibold uppercase tracking-wide ${t.tableHeadText} border-b ${t.border} whitespace-nowrap`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={t.tableDivide}>
          {calls.map((call) => (
            <tr
              key={call.id}
              className={`cursor-pointer group ${t.rowHover}`}
              onClick={() => onSelectCall(call)}
            >
              <td className={`px-2.5 py-2 text-[10px] font-mono ${t.muted}`}>{call.seq}</td>
              <td className="px-2.5 py-2">
                <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium border ${CALL_TYPE_COLORS[call.type]}`}>
                  {CALL_TYPE_LABELS[call.type]}
                </span>
              </td>
              <td className="px-2.5 py-2 min-w-[140px]">
                <div className={`flex items-center gap-1 text-[10px] ${t.textSecondary}`}>
                  <span className="truncate max-w-[72px]" title={call.from}>{call.from}</span>
                  <ArrowRight className={`w-3 h-3 shrink-0 ${t.iconMuted}`} />
                  <span className={`truncate max-w-[72px] font-medium ${t.text}`} title={call.to}>{call.to}</span>
                </div>
              </td>
              <td className={`px-2.5 py-2 text-[10px] font-medium ${t.text} whitespace-nowrap`}>{call.label}</td>
              <td className={`px-2.5 py-2 text-[10px] ${t.muted} max-w-[160px] truncate`} title={call.summary}>
                {call.summary}
              </td>
              <td className={`px-2.5 py-2 text-[10px] font-mono ${t.textSecondary}`}>{call.duration}</td>
              <td className={`px-2.5 py-2 text-[10px] font-mono ${t.muted}`}>
                {call.tokens?.toLocaleString() ?? '—'}
              </td>
              <td className={`px-2.5 py-2 text-[10px] font-mono ${t.muted}`}>{call.cost ?? '—'}</td>
              <td className="px-2.5 py-2">
                <div className="flex items-center gap-1">
                  <SpanStatusDot status={call.status} />
                  <SpanStatusText status={call.status} />
                </div>
              </td>
              <td className="px-2.5 py-2">
                <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity ${t.link}`}>
                  Simulate <ChevronRight className="w-3 h-3" />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
